<template>
  <div class="container-fluid">
    <tenant-info />
    <b-form
      autocomplete="off"
      class="o-form"
      @submit="onSubmit"
      @reset="onReset"
    >
      <!-- buttons -->
      <b-row>
        <b-col>
          <b-alert
            v-model="showAlert"
            variant="danger"
            dismissible
          >
            {{ alertMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row class="pb-3 justify-content-around">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>

      <!------------------------- Form ---------------------->
      <b-row>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.enabled"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
          />Enabled
        </b-col>
      </b-row>
      <b-row>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group
            class="o-field"
            :state="clientIdState"
          >
            <label
              for="clientId"
              class="text-truncate"
            >Client ID</label>
            <b-form-input
              id="clientId"
              v-model="form.clientId"
              placeholder="Client ID"
              :state="clientIdState"
              trim
              @keyup="throttledMethod(validateUniqueClientId)"
            />
            <div
              v-show="!clientIdState"
              class="invalid-tooltip"
            >
              {{ clientIdInvalidFeedback }}
            </div>
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="clientName"
              class="text-truncate"
            >Client Name</label>
            <b-form-input
              id="clientName"
              v-model="form.clientName"
              placeholder="Client Name"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          class="my-2"
          lg="2"
          md="12"
          hidden
        >
          <b-form-group class="o-field">
            <label
              for="protocolType"
              class="text-truncate"
            >Protocol Type</label>
            <b-select
              id="protocolType"
              v-model="form.protocolType"
              :options="protocolTypeOptions"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group class="o-field">
            <label
              for="description"
              class="text-truncate"
            >Description</label>
            <b-form-input
              id="description"
              v-model="form.description"
              placeholder="Description"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row hidden>
        <b-col
          class="my-2"
          lg="2"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="clientClaimsPrefix"
              class="text-truncate"
            >Client Claims Prefix</label>
            <b-form-input
              id="clientClaimsPrefix"
              v-model="form.clientClaimsPrefix"
              placeholder="Client Claims Prefix"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="4"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="pairWiseSubjectSalt"
              class="text-truncate"
            >Pair Wise Subject Salt</label>
            <b-form-input
              id="pairWiseSubjectSalt"
              v-model="form.pairWiseSubjectSalt"
              placeholder="Pair Wise Subject Salt"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="userCodeType"
              class="text-truncate"
            >User Code Type</label>
            <b-form-input
              id="userCodeType"
              v-model="form.userCodeType"
              placeholder="User Code Type"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row hidden>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="clientUri"
              class="text-truncate"
            >Client Uri</label>
            <b-form-input
              id="clientUri"
              v-model="form.clientUri"
              placeholder="Client Uri"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="logoUri"
              class="text-truncate"
            >Logo Uri</label>
            <b-form-input
              id="logoUri"
              v-model="form.logoUri"
              placeholder="Logo Uri"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row hidden>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="fcLogoutUri"
              class="text-truncate"
            >Front Channel Logout Uri</label>
            <b-form-input
              id="fcLogoutUri"
              v-model="form.frontChannelLogoutUri"
              placeholder="Front Channel Logout Uri"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="6"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="bcLogoutUri"
              class="text-truncate"
            >Back Channel Logout Uri</label>
            <b-form-input
              id="bcLogoutUri"
              v-model="form.backChannelLogoutUri"
              placeholder="Back Channel Logout Uri"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <!--------------------- hidden row: toggles ----------------------->
      <b-row hidden>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.allowAccessTokensViaBrowser"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
          />Allow Access-Tokens via Browser
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.requireConsent"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Require Consent
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.updateAccessTokenClaimsOnRefresh"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Update Access Token Claims on Refresh
        </b-col>
      </b-row>
      <!--------------------- hidden row: toggles ----------------------->
      <b-row hidden>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.alwaysSendClientClaims"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Always Send Client Claims
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.backChannelLogoutSessionRequired"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Back Channel Logout Session Required
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.enableLocalLogin"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Enable Local Login
        </b-col>
      </b-row>
      <!--------------------- hidden row: toggles ----------------------->
      <b-row hidden>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.frontChannelLogoutSessionRequired"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Front Channel Logout Session Required
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.includeJwtId"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Include Jwt Id
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.nonEditable"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Non-Editable
        </b-col>
      </b-row>
      <!--------------------- hidden row: toggles ----------------------->
      <b-row hidden>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.allowPlainTextPkce"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Allow Plain-Text Pkce
        </b-col>
        <b-col
          lg="4"
          md="12"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.allowRememberConsent"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Allow Remember Consent
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="3"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.allowOfflineAccess"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Allow Offline Access
        </b-col>
        <b-col
          cols="3"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.requirePkce"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Require Pkce
        </b-col>
        <b-col
          cols="3"
          class="my-2 o-label-sm o-strong text-truncate"
        >
          <toggle-button
            v-model="form.requireClientSecret"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Require Client Secret
        </b-col>
        <b-col
          cols="3"
          class="my-2 o-label-sm o-strong"
        >
          <toggle-button
            v-model="form.alwaysIncludeUserClaimsInIdToken"
            class="mb-0 mr-2"
            :height="24"
            :width="60"
            :sync="true"
            style="margin-right: 16px"
          />Include UserClaims in Id-Token
        </b-col>
      </b-row>

      <!------------------- hidden form fields ------------------------>
      <b-row hidden>
        <b-col
          class="my-2"
          lg="3"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="accessTokenType"
              class="text-truncate"
            >Access Token Type</label>
            <b-select
              id="accessTokenType"
              v-model="form.accessTokenType"
              :options="accessTokenTypeOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="3"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="refreshTokenUsage"
              class="text-truncate"
            >Refresh Token Usage</label>
            <b-select
              id="refreshTokenUsage"
              v-model="form.refreshTokenUsage"
              :options="refreshTokenUsageOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="3"
          md="12"
        >
          <b-form-group
            class="o-field"
            label="Consent Lifetime:"
            label-for="consentLifetime"
          >
            <label
              for="consentLifetime"
              class="text-truncate"
            >Consent Lifetime</label>
            <b-form-input
              id="consentLifetime"
              v-model="form.consentLifetime"
              placeholder="Consent Lifetime"
            />
          </b-form-group>
        </b-col>
        <b-col
          class="my-2"
          lg="3"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="userSsoLifetime"
              class="text-truncate"
            >User SSO Lifetime</label>
            <b-form-input
              id="userSsoLifetime"
              v-model="form.userSsoLifetime"
              placeholder="User SSO Lifetime"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row hidden>
        <b-col
          class="my-2"
          lg="3"
          md="12"
        >
          <b-form-group class="o-field">
            <label
              for="accessTokenType"
              class="text-truncate"
            >Access Token Type</label>
            <b-select
              id="accessTokenType"
              v-model="form.accessTokenType"
              :options="accessTokenTypeOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!------------------------------------ Client Secrets ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addClientSecret()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addClientSecret"
              v-b-toggle.collapse-1
              variant="link"
              :disabled="!hasClientSecrets"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Secrets&nbsp;
              <i
                v-if="hasClientSecrets"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasClientSecrets"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-1"
            v-model="collapseClientSecrets"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(clientSecret, clientSecretIndex) in form.clientSecrets"
                :key="'clientSecret.id' + clientSecretIndex"
                class="my-2"
              >
                <b-col cols="9">
                  <b-form-group class="o-field">
                    <label
                      :for="'value' + clientSecretIndex"
                      class="text-truncate"
                    >Value</label>
                    <b-form-input
                      :id="'value' + clientSecretIndex"
                      v-model="clientSecret.value"
                      :type="clientSecret.isNew ? 'text' : 'password'"
                      :disabled="clientSecret.isNew ? false : true"
                      required
                      placeholder="Value"
                      trim
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  :hidden="isHiddenField"
                  sm="12"
                  lg="2"
                  class="my-1 my-lg-0"
                >
                  <b-form-group class="o-field">
                    <label
                      :for="'type' + clientSecretIndex"
                      class="text-truncate"
                    >Type</label>
                    <b-select
                      :id="'type' + clientSecretIndex"
                      v-model="clientSecret.type"
                      :options="secretTypeOptions"
                      placeholder="Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="2">
                  <label
                    :for="'expiration' + clientSecretIndex"
                    class="datepickerlabel"
                  >Expiration</label>
                  <datepicker
                    :id="'expiration' + clientSecretIndex"
                    v-model="clientSecret.expiration"
                    class="o-field"
                    placeholder="Expiration"
                    :bootstrap-styling="true"
                    :disabled-dates="disabledDates"
                  >
                    <span slot="beforeCalendarHeader">
                      <b-container>
                        <b-row>
                          <b-col align="center">Expiration Date</b-col>
                        </b-row>
                        <b-row>
                          <b-col align="left">
                            <b-button
                              v-b-tooltip.hover
                              class
                              title="Go to today"
                              variant="success"
                              @click="setExpirationDateToday(clientSecretIndex)"
                            >Today</b-button>
                          </b-col>
                          <b-col align="right">
                            <b-button
                              v-b-tooltip.hover
                              class
                              title="Clears the 'Expiration' date"
                              variant="dark"
                              @click="clearExpirationDate(clientSecretIndex)"
                            >Clear</b-button>
                          </b-col>
                        </b-row>
                      </b-container>
                    </span>
                  </datepicker>
                </b-col>

                <b-col
                  cols="4"
                  sm="12"
                  lg="4"
                  class="my-1 my-lg-0"
                  hidden
                >
                  <b-form-group class="o-field">
                    <label
                      :for="'description' + clientSecretIndex"
                      class="text-truncate"
                    >Description</label>
                    <b-form-input
                      :id="'description' + clientSecretIndex"
                      v-model="clientSecret.description"
                      placeholder="Description"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="1"
                  class="my-1 my-lg-0"
                >
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteClientSecret(clientSecretIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Redirect Uris ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addRedirectUri()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addRedirectUri"
              v-b-toggle.collapse-2
              variant="link"
              :disabled="!hasRedirectUris"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Redirect URIs&nbsp;
              <i
                v-if="hasRedirectUris"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasRedirectUris"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-2"
            v-model="collapseRedirectUris"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(redirectUri, redirectUriIndex) in form.redirectUris"
                :key="'redirectUri.id' + redirectUriIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'redirectUri' + redirectUriIndex"
                      class="text-truncate"
                    >Redirect URI</label>
                    <b-form-input
                      :id="'redirectUri' + redirectUriIndex"
                      v-model="redirectUri.redirectUri"
                      required
                      placeholder="Redirect URI"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteRedirectUri(redirectUriIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Post Logout Redirect Uris ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addPostLogoutRedirectUri()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addPostLogoutRedirectUri"
              v-b-toggle.collapse-3
              variant="link"
              :disabled="!hasPostLogoutRedirectUris"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Post Logout Redirect URIs&nbsp;
              <i
                v-if="hasPostLogoutRedirectUris"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasPostLogoutRedirectUris"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-3"
            v-model="collapsePostLogoutRedirectUris"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(postLogoutRedirectUri,
                        postLogoutRedirectUriIndex) in form.postLogoutRedirectUris"
                :key="'postLogoutRedirectUri.id' + postLogoutRedirectUriIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="
                        'postLogoutRedirectUri' + postLogoutRedirectUriIndex
                      "
                      class="text-truncate"
                    >Post Logout Redirect Uri</label>
                    <b-form-input
                      :id="'postLogoutRedirectUri' + postLogoutRedirectUriIndex"
                      v-model="postLogoutRedirectUri.postLogoutRedirectUri"
                      required
                      placeholder="Post Logout Redirect Uri"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="
                      deletePostLogoutRedirectUri(postLogoutRedirectUriIndex)
                    "
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Claims ---------------------------------------->
      <b-row hidden>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addClaim()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addClaim"
              v-b-toggle.collapse-4
              variant="link"
              :disabled="!hasClaims"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Claims&nbsp;
              <i
                v-if="hasClaims"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasClaims"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-4"
            v-model="collapseClaims"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(claim, claimIndex) in form.claims"
                :key="'claim' + claimIndex"
                class="my-2"
              >
                <b-col
                  sm="12"
                  lg="5"
                  class="my-1 my-lg-0"
                >
                  <b-form-group class="o-field">
                    <label
                      :for="'claimType' + claimIndex"
                      class="text-truncate"
                    >Type</label>
                    <b-form-input
                      :id="'claimType' + claimIndex"
                      v-model="claim.type"
                      required
                      placeholder="Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="11"
                  class="my-1 my-lg-0"
                >
                  <b-form-group class="o-field">
                    <label
                      :for="'claimValue' + claimIndex"
                      class="text-truncate"
                    >Value</label>
                    <b-form-input
                      :id="'claimValue' + claimIndex"
                      v-model="claim.value"
                      required
                      placeholder="Value"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="1"
                  class="my-1 my-lg-0"
                >
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteClaim(claimIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
                <b-col cols="9" />
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Allowed Grant Types ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addAllowedGrantType()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addAllowedGrantType"
              v-b-toggle.collapse-5
              variant="link"
              :disabled="!hasAllowedGrantTypes"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Allowed Grant Types&nbsp;
              <i
                v-if="hasAllowedGrantTypes"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasAllowedGrantTypes"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-5"
            v-model="collapseAllowedGrantTypes"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(allowedGrantType,
                        allowedGrantTypeIndex) in form.allowedGrantTypes"
                :key="'allowedGrantType.id' + allowedGrantTypeIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'grantType' + allowedGrantTypeIndex"
                      class="text-truncate"
                    >Allowed Grant Type</label>
                    <b-form-input
                      :id="'grantType' + allowedGrantTypeIndex"
                      v-model="allowedGrantType.grantType"
                      required
                      placeholder="Allowed Grant Type"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteAllowedGrantType(allowedGrantTypeIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Allowed Scopes ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addAllowedScope()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addAllowedScope"
              v-b-toggle.collapse-6
              variant="link"
              :disabled="!hasAllowedScopes"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Allowed Scopes&nbsp;
              <i
                v-if="hasAllowedScopes"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasAllowedScopes"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-6"
            v-model="collapseAllowedScopes"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(allowedScope, allowedScopeIndex) in form.allowedScopes"
                :key="'allowedScope.id' + allowedScopeIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'scope' + allowedScopeIndex"
                      class="text-truncate"
                    >Allowed Scope</label>
                    <b-form-input
                      :id="'scope' + allowedScopeIndex"
                      v-model="allowedScope.scope"
                      required
                      placeholder="Allowed Scope"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteAllowedScope(allowedScopeIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Allowed Cors Origins ---------------------------------------->
      <b-row>
        <b-col class="pb-3">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addAllowedCorsOrigin()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addAllowedCorsOrigin"
              v-b-toggle.collapse-7
              variant="link"
              :disabled="!hasAllowedCorsOrigins"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Allowed Cors Origins&nbsp;
              <i
                v-if="hasAllowedCorsOrigins"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasAllowedCorsOrigins"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-7"
            v-model="collapseAllowedCorsOrigins"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(allowedCorsOrigin,
                        allowedCorsOriginIndex) in form.allowedCorsOrigins"
                :key="'allowedCorsOrigin.id' + allowedCorsOriginIndex"
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="'corsOrigin' + allowedCorsOriginIndex"
                      class="text-truncate"
                    >Allowed Cors Origin</label>
                    <b-form-input
                      :id="'corsOrigin' + allowedCorsOriginIndex"
                      v-model="allowedCorsOrigin.origin"
                      required
                      placeholder="Allowed Cors Origin"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="deleteAllowedCorsOrigin(allowedCorsOriginIndex)"
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!------------------------------------ Identity Provider Restrictions ---------------------------------------->
      <b-row hidden>
        <b-col class="pb-3 pt-1">
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="addIdentityProviderRestriction()"
            >
              <i class="fa fa-plus" />
            </b-button>
            <b-button
              id="addIdentityProviderRestriction"
              v-b-toggle.collapse-8
              variant="link"
              :disabled="!hasIdentityProviderRestrictions"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Identity Provider Restrictions&nbsp;
              <i
                v-if="hasIdentityProviderRestrictions"
                class="fa fa-caret-up"
              />
              <i
                v-if="hasIdentityProviderRestrictions"
                class="fa fas fa-check-circle mr-1 ml-4"
              />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-8"
            v-model="collapseIdentityProviderRestrictions"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row
                v-for="(identityProviderRestriction,
                        identityProviderRestrictionIndex) in form.identityProviderRestrictions"
                :key="
                  'identityProviderRestriction.id' +
                    identityProviderRestrictionIndex
                "
                class="my-2"
              >
                <b-col cols="11">
                  <b-form-group class="o-field">
                    <label
                      :for="
                        'providerRestriction' + identityProviderRestrictionIndex
                      "
                      class="text-truncate"
                    >Identity Provider Restriction</label>
                    <b-form-input
                      :id="
                        'providerRestriction' + identityProviderRestrictionIndex
                      "
                      v-model="identityProviderRestriction.provider"
                      required
                      placeholder="Identity Provider Restriction"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="1">
                  <br style="clear: both">
                  <b-button
                    v-b-tooltip.hover
                    class="btn-sm o-btn-trash"
                    title="Delete Row"
                    @click="
                      deleteIdentityProviderRestriction(
                        identityProviderRestrictionIndex
                      )
                    "
                  >
                    <i class="fa fa-trash" />
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>
      <!------------------- form fields ------------------------>
      <b-row>
        <b-col>
          <b-col class="p-0">
            <b-button
              class="o-btn-add"
              @click="toggleCollapseAdvanced"
            >
              <i class="fa fa-bars" />
            </b-button>
            <b-button
              id="expand"
              v-b-toggle.collapse-9
              variant="link"
              class="px-3 py-0 o-btn-link o-strong"
            >
              Advanced Options&nbsp;
              <i class="fa fa-caret-up" />
            </b-button>
          </b-col>
          <b-collapse
            id="collapse-9"
            v-model="collapseAdvanced"
          >
            <b-card>
              <div class="o-arrow-down" />
              <div class="o-arrow-down-border" />
              <b-row>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="absoluteRefreshTokenLifetime"
                      class="text-truncate"
                    >Absolute Refresh Token Lifetime</label>
                    <b-form-input
                      id="absoluteRefreshTokenLifetime"
                      v-model.number="form.absoluteRefreshTokenLifetime"
                      required
                      placeholder="Absolute Refresh Token Lifetime"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="slidingRefreshTokenLifetime"
                      class="text-truncate"
                    >Sliding Refresh Token Lifetime</label>
                    <b-form-input
                      id="slidingRefreshTokenLifetime"
                      v-model.number="form.slidingRefreshTokenLifetime"
                      required
                      placeholder="Sliding Refresh Token Lifetime"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="refreshTokenExpiration"
                      class="text-truncate"
                    >Refresh Token Expiration</label>
                    <b-form-input
                      id="refreshTokenExpiration"
                      v-model.number="form.refreshTokenExpiration"
                      required
                      placeholder="Refresh Token Expiration"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="identityTokenLifetime"
                      class="text-truncate"
                    >Identity Token Lifetime</label>
                    <b-form-input
                      id="identityTokenLifetime"
                      v-model.number="form.identityTokenLifetime"
                      required
                      placeholder="Identity Token Lifetime"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="accessTokenLifetime"
                      class="text-truncate"
                    >Access Token Lifetime</label>
                    <b-form-input
                      id="accessTokenLifetime"
                      v-model.number="form.accessTokenLifetime"
                      required
                      placeholder="Access Token Lifetime"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="deviceCodeLifetime"
                      class="text-truncate"
                    >Device Code Lifetime</label>
                    <b-form-input
                      id="deviceCodeLifetime"
                      v-model.number="form.deviceCodeLifetime"
                      required
                      placeholder="Device Code Lifetime"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="authorizationCodeLifetime"
                      class="text-truncate"
                    >Authorization Code Lifetime</label>
                    <b-form-input
                      id="authorizationCodeLifetime"
                      v-model.number="form.authorizationCodeLifetime"
                      required
                      placeholder="Authorization Code Lifetime"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  cols="3"
                  class="my-auto pt-3 o-label-sm o-strong text-truncate"
                >
                    <toggle-button
                      v-model="form.requireDPoP"
                      class="mb-0 mr-2"
                      :height="24"
                      :width="60"
                      :sync="true"
                      style="margin-right: 16px"
                    />Require DPoP
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="dPoPValidationMode"
                      class="text-truncate"
                    >DPoP Validation Mode</label>
                    <b-select
                      id="dPoPValidationMode"
                      v-model="form.dPoPValidationMode"
                      placeholder="Select"
                      :options="dPoPValidationModeOptions"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="dPoPClockSkew"
                      class="text-truncate"
                    >DPoP Clock Skew</label>
                    <b-form-input
                      id="dPoPClockSkew"
                      v-model="form.dPoPClockSkew"
                      required
                      placeholder="DPoP Clock Skew"
                      :state="isDPoPClockSkewValid"
                      @keyup="trackDPoPClockSkewState"
                      style="background-image: none !important"
                    />
                    <div v-show="!isDPoPClockSkewValid" class="invalid-tooltip">
                      Must use a format of hh:mm:ss
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  cols="3"
                  class="my-auto pt-3 o-label-sm o-strong text-truncate"
                >
                    <toggle-button
                      v-model="form.requirePushedAuthorization"
                      class="mb-0 mr-2"
                      :height="24"
                      :width="60"
                      :sync="true"
                      style="margin-right: 16px"
                    />Require Pushed Authorization
                </b-col>
                <b-col
                  class="my-2"
                  lg="3"
                  md="12"
                >
                  <b-form-group class="o-field">
                    <label
                      for="pushedAuthorizationLifetime"
                      class="text-truncate"
                    >Pushed Authorization Lifetime</label>
                    <b-form-input
                      id="pushedAuthorizationLifetime"
                      v-model="form.pushedAuthorizationLifetime"
                      placeholder="Pushed Authorization Lifetime"
                      :state="isPushedAuthorizationLifetimeValid"
                      @keyup="trackPushedAuthorizationLifetimeState"
                      style="background-image: none !important"
                    />
                    <div v-show="!isPushedAuthorizationLifetimeValid" class="invalid-tooltip">
                      Must be a number of seconds or leave empty to use global configuration
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="9">
                  <b-form-group class="o-field">
                    <label
                      for="initiateLoginUri"
                      class="text-truncate"
                    >Initiate Login URI</label>
                    <b-form-input
                      id="initiateLoginUri"
                      v-model="form.initiateLoginUri"
                      placeholder="Initiate Login URI"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </b-col>
      </b-row>

      <!-- buttons -->
      <b-row>
        <b-col>
          <b-alert
            v-model="showAlert"
            variant="danger"
            dismissible
          >
            {{ alertMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row class="pb-3 justify-content-around">
        <b-col>
          <b-button
            size="sm"
            class="o-save"
            type="submit"
            variant="primary"
          >
            <i class="fa fa-floppy-o" />&nbsp; SAVE
          </b-button>
          <b-button
            size="sm"
            class="o-cancel"
            type="reset"
            variant="danger"
          >
            <i class="fa fa-times" />&nbsp; CANCEL
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import TenantInfo from "./TenantInfo.vue";
import Datepicker from "vuejs-datepicker";
import _ from "lodash";
import { mapGetters } from "vuex";
export default {
  name: "ClientDetailForm",
  components: {
    TenantInfo,
    Datepicker,
  },
  data() {
    return {
      isClientIdUnique: true,
      disabledDates: {
        to: new Date(),
      },
      disabledFn: {
        customPredictor(date) {
          let today = new Date();
          if (Date(date) < today) {
            return true;
          }
        },
      },
      isHiddenField: true, // hide fields in the ui for now.
      protocolTypeOptions: [{ value: "oidc", text: "OpenID Connect" }],
      secretTypeOptions: [{ value: "SharedSecret", text: "Shared Secret" }],
      refreshTokenUsageOptions: [
        { value: 0, text: "Re-Use" },
        { value: 1, text: "One Time Only" },
      ],
      accessTokenTypeOptions: [
        { value: 0, text: "Jwt" },
        { value: 1, text: "Reference" },
      ],
      collapseAdvanced: false,
      collapseClaims: false,
      collapseAllowedCorsOrigins: false,
      collapseAllowedGrantTypes: false,
      collapseIdentityProviderRestrictions: false,
      collapsePostLogoutRedirectUris: false,
      collapseRedirectUris: false,
      collapseAllowedScopes: false,
      collapseClientSecrets: false,
      isDPoPClockSkewValid: true,
      isPushedAuthorizationLifetimeValid: true,
      form: {
        id: 0,
        clientId: "",
        enabled: true,
        protocolType: "oidc",
        clientClaimsPrefix: "_client",
        requireClientSecret: true,
        requireConsent: false,
        allowRememberConsent: true,
        frontChannelLogoutSessionRequired: true,
        backChannelLogoutSessionRequired: true,
        absoluteRefreshTokenLifetime: 2592000,
        accessTokenLifetime: 3600,
        accessTokenType: 0,
        authorizationCodeLifetime: 300,
        deviceCodeLifetime: 300,
        identityTokenLifetime: 300,
        refreshTokenExpiration: 2592000,
        refreshTokenUsage: 1,
        slidingRefreshTokenLifetime: 1296000,
        consentLifetime: null,
        userSsoLifetime: null,
        enableLocalLogin: true,
        dPoPClockSkew: "00:00:00",
        dPoPValidationMode: 0,
        requireDPoP: false,
        pushedAuthorizationLifetime: null,
        requirePushedAuthorization: false,
        initiateLoginUri: null,
        claims: [],
        allowedCorsOrigins: [],
        allowedGrantTypes: [],
        identityProviderRestrictions: [],
        postLogoutRedirectUris: [],
        redirectUris: [],
        allowedScopes: [],
        clientSecrets: [],
      },
      dPoPValidationModeOptions: [
        { value: 0, text: "None" },
        { value: 1, text: "Iat" },
        { value: 2, text: "Nonce" },
        { value: 3, text: "Iat & Nonce" },
      ]
    };
  },
  computed: {
    ...mapGetters({
      isLoading: "isLoading",
    }),
    showAlert: {
      get() {
        return this.$store.getters.showAlert;
      },
      set(value) {
        this.$store.dispatch("showAlert", value);
      },
    },
    alertMessage: {
      get() {
        return this.$store.getters.alertMessage;
      },
      set(value) {
        this.$store.dispatch("alertMessage", value);
      },
    },
    hasClaims: function () {
      return this.form.claims.length > 0;
    },
    hasAllowedCorsOrigins: function () {
      return this.form.allowedCorsOrigins.length > 0;
    },
    hasAllowedGrantTypes: function () {
      return this.form.allowedGrantTypes.length > 0;
    },
    hasIdentityProviderRestrictions: function () {
      return this.form.identityProviderRestrictions.length > 0;
    },
    hasPostLogoutRedirectUris: function () {
      return this.form.postLogoutRedirectUris.length > 0;
    },
    hasRedirectUris: function () {
      return this.form.redirectUris.length > 0;
    },
    hasAllowedScopes: function () {
      return this.form.allowedScopes.length > 0;
    },
    hasClientSecrets: function () {
      return this.form.clientSecrets.length > 0;
    },
    // Validation - ClientId Field
    clientIdState() {
      return this.form.clientId.length >= 1 && this.isClientIdUnique;
    },
    clientIdInvalidFeedback() {
      if (this.form.clientId.length < 1) {
        return "Required";
      }
      if (!this.isClientIdUnique) {
        return "Must be unique across all Tenants";
      }
      return "?ERROR IN VALIDATION LOGIC?";
    },
  },
  created() {
    this.loadClients();
  },
  methods: {
    throttledMethod: function (func) {
      let debounced = _.debounce(func.bind(this), 500);
      debounced();
    },
    // validation
    validateUniqueClientId() {
      let value = this.form.clientId;
      if (value.length >= 4) {
        this.$axios
          .get(
            `/api/tenants/clients/${this.form.id}/isUnique/clientId/${value}`
          )
          .then((response) => {
            this.validationError = response.data;
            this.isClientIdUnique = response.data.valid;
            console.log("Result = " + this.isClientIdUnique);
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    toggleCollapseAdvanced() {
      this.collapseAdvanced = !this.collapseAdvanced;
    },
    disableFrom(val) {
      if (typeof this.disabledDates.from === "undefined") {
        this.disabled = {
          to: this.disabledDates.to,
          daysOfMonth: this.disabled.daysOfMonth,
          from: null,
        };
      }
      this.disabledDates.from = val;
    },
    loadClients() {
      if (this.$route.params.clientId > 0) {
        this.$axios
          .get(
            `/api/tenants/${this.$route.params.tenantId}/clients/${this.$route.params.clientId}`
          )
          .then((response) => {
            this.form = response.data;
            this.expandPanelsOnLoad();
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    clearExpirationDate(index) {
      this.form.clientSecrets[index].expiration = null;
    },
    setExpirationDateToday(index) {
      this.form.clientSecrets[index].expiration = new Date();
    },
    // ClientSecret
    deleteClientSecret(index) {
      this.form.clientSecrets.splice(index, 1);
      this.collapseClientSecrets = this.hasClientSecrets;
    },
    addClientSecret() {
      if (this.form.clientSecrets === undefined) {
        this.form.clientSecrets = [];
      }
      this.form.clientSecrets.push({
        id: 0,
        type: "SharedSecret",
        value: this.generateSecret(),
        expiration: null,
        isNew: true,
      });
      this.collapseClientSecrets = this.hasClientSecrets;
    },
    // RedirectUri
    deleteRedirectUri(index) {
      this.form.redirectUris.splice(index, 1);
      this.collapseRedirectUris = this.hasRedirectUris;
    },
    addRedirectUri() {
      if (this.form.redirectUris === undefined) {
        this.form.redirectUris = [];
      }
      this.form.redirectUris.push({ id: 0 });
      this.collapseRedirectUris = this.hasRedirectUris;
    },
    // PostLogoutRedirectUri
    deletePostLogoutRedirectUri(index) {
      this.form.postLogoutRedirectUris.splice(index, 1);
      this.collapsePostLogoutRedirectUris = this.hasPostLogoutRedirectUris;
    },
    addPostLogoutRedirectUri() {
      if (this.form.postLogoutRedirectUris === undefined) {
        this.form.postLogoutRedirectUris = [];
      }
      this.form.postLogoutRedirectUris.push({ id: 0 });
      this.collapsePostLogoutRedirectUris = this.hasPostLogoutRedirectUris;
    },

    //claims
    deleteClaim(index) {
      this.form.claims.splice(index, 1);
      this.collapseClaims = this.hasClaims;
    },
    addClaim() {
      if (this.form.claims === undefined) {
        this.form.claims = [];
      }
      this.form.claims.push({ id: 0 });
      this.collapseClaims = this.hasClaims;
    },

    //allowedGrantTypes
    deleteAllowedGrantType(index) {
      this.form.allowedGrantTypes.splice(index, 1);
      this.collapseAllowedGrantTypes = this.hasAllowedGrantTypes;
    },
    addAllowedGrantType() {
      if (this.form.allowedGrantTypes === undefined) {
        this.form.allowedGrantTypes = [];
      }
      this.form.allowedGrantTypes.push({ id: 0 });
      this.collapseAllowedGrantTypes = this.hasAllowedGrantTypes;
    },

    //allowedScopes
    deleteAllowedScope(index) {
      this.form.allowedScopes.splice(index, 1);
      this.collapseAllowedScopes = this.hasAllowedScopes;
    },
    addAllowedScope() {
      if (this.form.allowedScopes === undefined) {
        this.form.allowedScopes = [];
      }
      this.form.allowedScopes.push({ id: 0 });
      this.collapseAllowedScopes = this.hasAllowedScopes;
    },

    //allowedCorsOrigins
    deleteAllowedCorsOrigin(index) {
      this.form.allowedCorsOrigins.splice(index, 1);
      this.collapseAllowedCorsOrigins = this.hasAllowedCorsOrigins;
    },
    addAllowedCorsOrigin() {
      if (this.form.allowedCorsOrigins === undefined) {
        this.form.allowedCorsOrigins = [];
      }
      this.form.allowedCorsOrigins.push({ id: 0 });
      this.collapseAllowedCorsOrigins = this.hasAllowedCorsOrigins;
    },

    //identityProviderRestrictions
    deleteIdentityProviderRestriction(index) {
      this.form.identityProviderRestrictions.splice(index, 1);
      this.collapseIdentityProviderRestrictions = this.hasIdentityProviderRestrictions;
    },
    addIdentityProviderRestriction() {
      if (this.form.identityProviderRestrictions === undefined) {
        this.form.identityProviderRestrictions = [];
      }
      this.form.identityProviderRestrictions.push({ id: 0 });
      this.collapseIdentityProviderRestrictions = this.hasIdentityProviderRestrictions;
    },

    isFormValid() {
        return this.form.clientId.length >= 1 && this.isClientIdUnique && this.isPushedAuthorizationLifetimeValid && this.isDPoPClockSkewValid;
    },

    ////////////////////////////////////////////////////
    onSubmit(evt) {
      evt.preventDefault();
      this.alertMessage = "";

      if (this.form.pushedAuthorizationLifetime === '') {
        this.form.pushedAuthorizationLifetime = null;
      }

      if (!this.isFormValid()) {
        this.alertMessage = "Validation issues must be fixed before saving";
        return;
      }

      if (this.$route.params.clientId == undefined) {
        this.$axios
          .post(
            `/api/tenants/${this.$route.params.tenantId}/clients/new`,
            this.form
          )
          .then(() => {
            this.$router.go(-1);
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      } else {
        this.$axios
          .put(
            `/api/tenants/${this.$route.params.tenantId}/clients/${this.$route.params.clientId}/edit`,
            this.form
          )
          .then(() => {
            this.$router.go(-1);
          })
          .catch((error) => {
            this.alertMessage = error;
          });
      }
    },
    onReset(evt) {
      evt.preventDefault();
      this.alertMessage = "";
      this.$router.go(-1);
    },
    expandPanelsOnLoad() {
      this.collapseClientSecrets = this.hasClientSecrets;
      this.collapseRedirectUris = this.hasRedirectUris;
      this.collapsePostLogoutRedirectUris = this.hasPostLogoutRedirectUris;
      this.collapseClaims = this.hasClaims;
      this.collapseAllowedGrantTypes = this.hasAllowedGrantTypes;
      this.collapseAllowedScopes = this.hasAllowedScopes;
      this.collapseAllowedCorsOrigins = this.hasAllowedCorsOrigins;
      this.collapseIdentityProviderRestrictions = this.hasIdentityProviderRestrictions;
    },
    trackDPoPClockSkewState() {
      const dPoPClockSkew = this.form.dPoPClockSkew;
      this.isDPoPClockSkewValid = /^[0-2][0-3]:[0-5]\d:[0-5]\d$/.test(dPoPClockSkew);
    },
    trackPushedAuthorizationLifetimeState() {
      const pushedAuthorizationLifetime = this.form.pushedAuthorizationLifetime;
      this.isPushedAuthorizationLifetimeValid = !pushedAuthorizationLifetime || /^\d+$/.test(pushedAuthorizationLifetime);
    }
  }
};
</script>
